import React from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/client";
import AppIcon from "../components/AppIcon";
import { PasswordCriteriaInfo } from "../components/ui-helpers";

function readApiError(error, fallback) {
  return error?.response?.data?.error || error?.response?.data?.message || fallback;
}

const STEP_META = [
  { number: 1, key: "request", label: "Request OTP" },
  { number: 2, key: "verify", label: "Verify" },
  { number: 3, key: "reset", label: "New password" },
  { number: 4, key: "pin", label: "PIN" },
];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [uidOrEmail, setUidOrEmail] = React.useState("");
  const [uid, setUid] = React.useState("");
  const [step, setStep] = React.useState(1);
  const [otp, setOtp] = React.useState("");
  const [token, setToken] = React.useState("");
  const [pwd1, setPwd1] = React.useState("");
  const [pwd2, setPwd2] = React.useState("");
  const [pin1, setPin1] = React.useState("");
  const [pin2, setPin2] = React.useState("");
  const [pinOptional, setPinOptional] = React.useState(false);
  const [canResendAt, setCanResendAt] = React.useState(0);
  const [banner, setBanner] = React.useState({ kind: "info", text: "" });
  const [busyAction, setBusyAction] = React.useState("");
  const [, setTick] = React.useState(0);

  const cooldown = Math.max(0, Math.ceil((canResendAt - Date.now()) / 1000));
  const isBusy = Boolean(busyAction);

  React.useEffect(() => {
    document.title = "QualiGate — Forgot password";
  }, []);

  React.useEffect(() => {
    if (step !== 2 || cooldown <= 0) return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 250);
    return () => window.clearInterval(id);
  }, [step, cooldown]);

  const startCooldown = (seconds = 60) => setCanResendAt(Date.now() + Math.max(0, Number(seconds) || 60) * 1000);

  const goBackOneStep = () => {
    setBanner({ kind: "info", text: "" });
    setBusyAction("");
    setStep((current) => Math.max(1, current - 1));
  };

  const requestOtp = async () => {
    if (!uidOrEmail.trim() || isBusy) return;
    setBusyAction("request");
    setBanner({ kind: "info", text: "" });
    try {
      const form = new FormData();
      if (uidOrEmail.includes("@")) form.append("email", uidOrEmail.trim());
      else form.append("uid", uidOrEmail.trim());
      const response = await api.post("/auth/request_reset", form);
      const resolvedUid = response.data?.uid || (uidOrEmail.includes("@") ? "" : uidOrEmail.trim());
      setUid(resolvedUid);
      setPinOptional(Boolean(response.data?.pin_optional));
      setOtp("");
      setToken("");
      setPwd1("");
      setPwd2("");
      setPin1("");
      setPin2("");
      startCooldown(response.data?.resend_cooldown_seconds);
      setStep(2);
      const ttl = response.data?.otp_ttl_minutes ? ` It expires in ${response.data.otp_ttl_minutes} minute(s).` : "";
      setBanner({ kind: "success", text: `A 6-digit OTP was sent to the email address on file for ${resolvedUid || uidOrEmail.trim()}.${ttl}` });
    } catch (error) {
      setBanner({ kind: "error", text: readApiError(error, "Could not send OTP.") });
    } finally {
      setBusyAction("");
    }
  };

  const resendOtp = async () => {
    if (cooldown > 0 || isBusy) return;
    setBusyAction("resend");
    setBanner({ kind: "info", text: "" });
    try {
      const form = new FormData();
      if (uidOrEmail.includes("@")) form.append("email", uidOrEmail.trim());
      else form.append("uid", uidOrEmail.trim());
      const response = await api.post("/auth/request_reset", form);
      if (response.data?.uid) setUid(response.data.uid);
      if (typeof response.data?.pin_optional === "boolean") setPinOptional(Boolean(response.data.pin_optional));
      startCooldown(response.data?.resend_cooldown_seconds);
      const ttl = response.data?.otp_ttl_minutes ? ` It expires in ${response.data.otp_ttl_minutes} minute(s).` : "";
      setBanner({ kind: "success", text: `A fresh OTP has been sent.${ttl}` });
    } catch (error) {
      setBanner({ kind: "error", text: readApiError(error, "Could not resend OTP.") });
    } finally {
      setBusyAction("");
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6 || isBusy) return;
    setBusyAction("verify");
    setBanner({ kind: "info", text: "" });
    try {
      const form = new FormData();
      form.append("uid", uid || uidOrEmail.trim());
      form.append("otp", otp);
      const response = await api.post("/auth/verify_otp", form);
      if (!response.data?.ok) {
        setBanner({ kind: "error", text: "The OTP is invalid or has expired." });
        return;
      }
      if (response.data?.uid) setUid(response.data.uid);
      if (typeof response.data?.pin_optional === "boolean") setPinOptional(Boolean(response.data.pin_optional));
      setToken(response.data.token || "");
      setStep(3);
      const grantTtl = response.data?.grant_ttl_minutes ? ` The reset grant expires in ${response.data.grant_ttl_minutes} minute(s).` : "";
      setBanner({ kind: "success", text: `OTP verified. Set your new password to continue.${grantTtl}` });
    } catch (error) {
      setBanner({ kind: "error", text: readApiError(error, "Could not verify OTP.") });
    } finally {
      setBusyAction("");
    }
  };

  const resetPassword = async () => {
    if (isBusy) return;
    if (!pwd1 || !pwd2) {
      setBanner({ kind: "error", text: "Enter and confirm your new password." });
      return;
    }
    if (pwd1 !== pwd2) {
      setBanner({ kind: "error", text: "Passwords do not match." });
      return;
    }

    setBusyAction("reset");
    setBanner({ kind: "info", text: "" });
    try {
      const form = new FormData();
      form.append("uid", uid || uidOrEmail.trim());
      form.append("token", token);
      form.append("new_password", pwd1);
      await api.post("/auth/reset_password", form);
      setStep(4);
      setBanner({
        kind: "success",
        text: pinOptional
          ? "Password updated. You can set a 6-digit PIN now or skip and return to login."
          : "Password updated. Set your 6-digit PIN to finish the reset flow.",
      });
    } catch (error) {
      setBanner({ kind: "error", text: readApiError(error, "Could not reset password.") });
    } finally {
      setBusyAction("");
    }
  };

  const savePin = async () => {
    if (isBusy) return;
    if (pin1.length !== 6 || pin2.length !== 6) {
      setBanner({ kind: "error", text: "PIN must be a 6-digit number." });
      return;
    }
    if (pin1 !== pin2) {
      setBanner({ kind: "error", text: "PINs do not match." });
      return;
    }

    setBusyAction("pin");
    setBanner({ kind: "info", text: "" });
    try {
      const form = new FormData();
      form.append("uid", uid || uidOrEmail.trim());
      form.append("role", "");
      form.append("pin", pin1);
      form.append("confirm_pin", pin2);
      await api.post("/auth/set_pin", form);
      setStep(5);
      setBanner({ kind: "success", text: "Your password reset is complete. You can sign in with the updated credentials now." });
    } catch (error) {
      setBanner({ kind: "error", text: readApiError(error, "Could not save PIN.") });
    } finally {
      setBusyAction("");
    }
  };

  const finishWithoutPin = () => {
    setStep(5);
    setBanner({ kind: "success", text: "Your password reset is complete. You can return to login now." });
  };

  const currentStepTitle = {
    1: "Reset your password",
    2: "Verify your OTP",
    3: "Choose a new password",
    4: pinOptional ? "Set an optional PIN" : "Set your 6-digit PIN",
    5: "All set",
  }[step];

  const currentStepCopy = {
    1: "Enter the user ID or email associated with your QualiGate account.",
    2: "Enter the 6-digit OTP sent to your registered email address.",
    3: "Choose a new password for your account and keep the criteria handy from the info icon.",
    4: pinOptional ? "Set a 6-digit PIN now, or skip this step if your role does not require an E-PIN." : "This PIN protects sensitive approval and signature actions.",
    5: "Your reset flow is complete.",
  }[step];

  return (
    <div className="login-shell forgot-shell">
      <div className="login-backdrop-orb login-backdrop-orb--left" />
      <div className="login-backdrop-orb login-backdrop-orb--right" />
      <div className="login-banner" aria-hidden="true" />

      <div className="login-card-wrap auth-card-wrap">
        <div className="login-card-shell auth-card-shell premium-fade-in">
          <div className="auth-card-header-row">
            <Link className="text-link auth-back-link" to="/login">
              <AppIcon name="chevron-left" />
              <span>Back to login</span>
            </Link>
          </div>

          <div className="login-card login-form-card forgot-form-card">
            <div className="auth-badge">Secure account recovery</div>
            <h1 className="login-title auth-title">
              <img src="/brand/logo.png" alt="QualiGate" className="login-title-logo" />
              <span className="brand" aria-label="QualiGate">
                <span className="brand-quali">Quali</span><span className="brand-gate">Gate</span>
              </span>
            </h1>
            <div className="login-subcopy auth-subcopy">{currentStepTitle}</div>
            <div className="helper-text auth-helper-copy">{currentStepCopy}</div>

            <div className="auth-stepper" aria-label="Reset password progress">
              {STEP_META.map((item) => {
                const isComplete = step > item.number;
                const isCurrent = step === item.number;
                const isUpcoming = step < item.number;
                return (
                  <div key={item.key} className={`auth-step-pill${isCurrent ? " is-current" : ""}${isComplete ? " is-complete" : ""}${isUpcoming ? " is-upcoming" : ""}`}>
                    <span className="auth-step-pill-index">{item.number}</span>
                    <span>{item.label}</span>
                  </div>
                );
              })}
            </div>

            {banner.text ? <div className={`banner ${banner.kind}`}>{banner.text}</div> : null}

            {step === 1 ? (
              <div className="vstack auth-form-stack">
                <label>
                  <span>User ID or Email</span>
                  <input
                    autoFocus
                    placeholder="Enter your user ID or email"
                    value={uidOrEmail}
                    onChange={(event) => setUidOrEmail(event.target.value)}
                    disabled={isBusy}
                  />
                </label>
                <div className="auth-action-row">
                  <button className="btn btn-primary login-submit-btn" disabled={!uidOrEmail.trim() || isBusy} onClick={requestOtp} type="button">
                    {busyAction === "request" ? "Sending OTP…" : "Send OTP"}
                  </button>
                </div>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="vstack auth-form-stack">
                <label>
                  <span>6-digit OTP</span>
                  <input
                    autoFocus
                    placeholder="Enter the OTP"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                    disabled={isBusy}
                  />
                </label>
                <div className="auth-inline-note">Didn’t receive the email? Check your spam folder or request a fresh OTP.</div>
                <div className="auth-action-row auth-action-row--split">
                  <div className="auth-secondary-actions">
                    <button className="btn btn-secondary" type="button" onClick={goBackOneStep} disabled={isBusy}>Back</button>
                    <button className="btn btn-secondary" type="button" onClick={resendOtp} disabled={cooldown > 0 || isBusy}>
                      {busyAction === "resend" ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                  <button className="btn btn-primary" type="button" onClick={verifyOtp} disabled={otp.length !== 6 || isBusy}>
                    {busyAction === "verify" ? "Verifying…" : "Verify OTP"}
                  </button>
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="vstack auth-form-stack">
                <label>
                  <span className="field-label-inline">
                    <span>New password</span>
                    <PasswordCriteriaInfo />
                  </span>
                  <input type="password" value={pwd1} onChange={(event) => setPwd1(event.target.value)} placeholder="Enter a new password" disabled={isBusy} autoFocus />
                </label>
                <label>
                  <span>Confirm password</span>
                  <input type="password" value={pwd2} onChange={(event) => setPwd2(event.target.value)} placeholder="Re-enter the new password" disabled={isBusy} />
                </label>
                <div className="auth-action-row auth-action-row--split">
                  <button className="btn btn-secondary" type="button" onClick={goBackOneStep} disabled={isBusy}>Back</button>
                  <button className="btn btn-primary" type="button" onClick={resetPassword} disabled={!pwd1 || !pwd2 || isBusy}>
                    {busyAction === "reset" ? "Updating password…" : "Set new password"}
                  </button>
                </div>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="vstack auth-form-stack">
                <label>
                  <span>6-digit PIN</span>
                  <input
                    autoFocus
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="new-password"
                    value={pin1}
                    onChange={(event) => setPin1(event.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                    placeholder="Enter a 6-digit PIN"
                    disabled={isBusy}
                  />
                </label>
                <label>
                  <span>Confirm PIN</span>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="new-password"
                    value={pin2}
                    onChange={(event) => setPin2(event.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                    placeholder="Re-enter the 6-digit PIN"
                    disabled={isBusy}
                  />
                </label>
                {pin1 && !/^\d{6}$/.test(pin1) ? <div className="banner error">PIN must be a 6-digit number.</div> : null}
                {pin1 && pin2 && pin1 !== pin2 ? <div className="banner error">PINs do not match.</div> : null}
                <div className="auth-inline-note">Use a PIN you can remember. It is required for critical approval actions.</div>
                <div className="auth-action-row auth-action-row--split">
                  <div className="auth-secondary-actions">
                    {pinOptional ? (
                      <button className="btn btn-secondary" type="button" onClick={finishWithoutPin} disabled={isBusy}>Skip for now</button>
                    ) : null}
                  </div>
                  <button className="btn btn-primary" type="button" onClick={savePin} disabled={pin1.length !== 6 || pin2.length !== 6 || isBusy}>
                    {busyAction === "pin" ? "Saving PIN…" : "Save PIN"}
                  </button>
                </div>
              </div>
            ) : null}

            {step === 5 ? (
              <div className="vstack auth-form-stack">
                <div className="banner success">Your credentials are updated and ready to use.</div>
                <div className="auth-action-row">
                  <button className="btn btn-primary login-submit-btn" type="button" onClick={() => navigate("/login", { replace: true })}>
                    Return to login
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
